import { useEffect, useRef, useState } from "react";
import { Check, Loader2, RotateCcw, Sparkles, X } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { useLook } from "@/context/LookContext";
import { api, errMsg } from "@/lib/api";
import { useAuthedSrc } from "@/lib/authedImage";

const FAILED = "We couldn't make that one. Try again or change your idea.";

function Elapsed() {
  const [s, setS] = useState(0);
  useEffect(() => {
    const t0 = Date.now();
    const id = setInterval(() => setS(Math.floor((Date.now() - t0) / 1000)), 250);
    return () => clearInterval(id);
  }, []);
  return <span className="tabular-nums" data-testid="ai-wallpaper-elapsed">{s}s</span>;
}

export default function AiWallpaper({ onKept }) {
  const { session } = useAuth();
  const { update } = useLook();
  const [prompt, setPrompt] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState(null);
  const pendingRef = useRef(null);
  const src = useAuthedSrc(result ? `/me/wallpapers/${result.wallpaper.id}/file` : null);
  const isKid = session?.type === "kid";

  const discardPending = async () => {
    const id = pendingRef.current;
    pendingRef.current = null;
    if (id) await api.delete(`/me/wallpapers/${id}`).catch(() => {});
  };
  useEffect(() => () => { discardPending(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const make = async () => {
    setError("");
    if (!prompt.trim()) { setError("Tell us what to paint"); return; }
    setBusy(true);
    await discardPending();
    setResult(null);
    try {
      const { data } = await api.post("/me/wallpapers/generate", { prompt }, { timeout: 75000 });
      pendingRef.current = data.wallpaper.id;
      setResult(data);
    } catch (e) {
      setError(e.code === "ECONNABORTED" ? FAILED : errMsg(e, FAILED));
    } finally { setBusy(false); }
  };
  const use = () => {
    const w = result.wallpaper;
    pendingRef.current = null;
    update({ wallpaper: { type: "mine", id: w.id } });
    onKept({ ...w, kept: true });
    setResult(null);
    setPrompt("");
  };
  const discard = async () => { await discardPending(); setResult(null); };

  return (
    <div className="q-card space-y-4" data-testid="ai-wallpaper-panel">
      <div>
        <label className="q-label" htmlFor="ai-prompt">Describe your wallpaper</label>
        <textarea id="ai-prompt" rows={2} maxLength={300} className="q-input resize-none" value={prompt} disabled={busy}
          placeholder={isKid ? "a rainbow dragon in a candy forest" : "a cozy cabin by a lake at sunset"}
          onChange={(e) => setPrompt(e.target.value)} data-testid="ai-wallpaper-prompt" />
        {isKid && <p className="mt-1.5 text-xs text-q-muted">We keep every picture kid-friendly.</p>}
      </div>
      <div className="flex flex-wrap items-center gap-3">
        <button className="q-btn q-btn-primary" onClick={make} disabled={busy} data-testid="ai-wallpaper-make-btn">
          {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />} Make it
        </button>
        {busy && <p className="text-sm font-semibold text-q-text" data-testid="ai-wallpaper-painting">Painting your wallpaper… <span className="text-q-muted"><Elapsed /></span></p>}
        <span className="text-xs text-q-faint">Up to 10 a day</span>
      </div>
      {busy && <div className="relative h-1 overflow-hidden rounded-full bg-q-raised"><div className="q-scan-bar absolute inset-y-0 w-1/3 rounded-full bg-q-accent" /></div>}
      {error && <p className="rounded-lg border border-red-500/30 bg-red-500/10 px-3 py-2 text-sm font-semibold text-red-300" data-testid="ai-wallpaper-error">{error}</p>}
      {result && (
        <div className="space-y-3 q-rise" data-testid="ai-wallpaper-result">
          <div className="relative aspect-video overflow-hidden rounded-xl border border-q-line bg-q-raised">
            {src ? <img src={src} alt={result.wallpaper.title} className="h-full w-full object-cover" data-testid="ai-wallpaper-result-img" />
              : <Loader2 className="absolute inset-0 m-auto h-6 w-6 animate-spin text-q-faint" />}
          </div>
          <p className="text-xs text-q-muted">Made in <span data-testid="ai-wallpaper-seconds">{result.seconds}</span>s</p>
          <div className="flex flex-wrap gap-2">
            <button className="q-btn q-btn-primary" onClick={use} data-testid="ai-wallpaper-use-btn"><Check className="h-4 w-4" /> Use as wallpaper</button>
            <button className="q-btn q-btn-secondary" onClick={make} disabled={busy} data-testid="ai-wallpaper-retry-btn"><RotateCcw className="h-4 w-4" /> Try again</button>
            <button className="q-btn q-btn-ghost" onClick={discard} data-testid="ai-wallpaper-discard-btn"><X className="h-4 w-4" /> Discard</button>
          </div>
        </div>
      )}
    </div>
  );
}
